import type {
  AdhesionSummaryResponse,
  AdhesionSummaryResponseItem,
  HistoricoParecerPAResponse,
  MotivoRecusaPAResponse,
  ProponenteSubestipulanteListResponse,
  ValidacaoPAProponenteListResponse,
} from '../../types/vida/adhesionProposal/adhesionProposalType';
import { VIDA_ADHESION_PROPOSAL_ROUTES } from '../requestsService';

export class AdhesionProposalService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Processes subestipulante proponent list data to generate chart summary.
   * Groups data by statusProponente and calculates counts and percentages.
   * @param data - ProponenteSubestipulanteListResponse data
   * @returns AdhesionSummaryResponse with status counts and percentages
   */
  private processSummaryFromList(
    data: ProponenteSubestipulanteListResponse
  ): AdhesionSummaryResponse {
    const items = data.pagina.content;
    const total = data.pagina.totalElements;

    const statusMap = new Map<number, number>();
    items.forEach((item) => {
      const count = statusMap.get(item.statusProponente) || 0;
      statusMap.set(item.statusProponente, count + 1);
    });

    const summary: AdhesionSummaryResponseItem[] = Array.from(statusMap.entries()).map(
      ([status, count]) => ({
        status: status.toString(),
        count: count,
        percentage: total > 0 ? (count / total) * 100 : 0,
        total: total,
      })
    );

    return summary;
  }

  /**
   * Gets paginated list of subestipulante proponent proposals.
   * Matches backend endpoint: /v1/bff/vida/proposta-adesao/obter-lista-proponente-subestipulante
   * This endpoint returns data for both list and chart (summary).
   * @param identSubestipulante - Subestipulante identifier (required)
   * @param nmProponente - Proponent name (optional)
   * @param cdCpf - CPF code (optional)
   * @param cdCtrlCpf - CPF control digit (optional)
   * @param cdMatricula - Registration code (optional)
   * @param page - Page number (0-based, default: 0)
   * @param size - Page size (default: 6)
   */
  async getSubestipulanteProponentList(
    identSubestipulante: number,
    nmProponente?: string,
    cdCpf?: string,
    cdCtrlCpf?: string,
    cdMatricula?: number,
    page?: number,
    size?: number
  ): Promise<ProponenteSubestipulanteListResponse> {
    let url = `${VIDA_ADHESION_PROPOSAL_ROUTES.GET_LIST_PROPOSAL_SUBESTIPULANTE}?identSubestipulante=${encodeURIComponent(identSubestipulante)}`;

    if (nmProponente) {
      url += `&NmProponente=${encodeURIComponent(nmProponente)}`;
    }

    if (cdCpf) {
      url += `&CdCpf=${encodeURIComponent(cdCpf)}`;
    }

    if (cdCtrlCpf) {
      url += `&CdCtrlCpf=${encodeURIComponent(cdCtrlCpf)}`;
    }

    if (cdMatricula !== undefined && cdMatricula !== null) {
      url += `&CdMatricula=${encodeURIComponent(cdMatricula)}`;
    }

    if (page !== undefined && page !== null) {
      url += `&page=${page}`;
    }

    if (size !== undefined && size !== null) {
      url += `&size=${size}`;
    }

    return this.fetch<ProponenteSubestipulanteListResponse>(url, {
      method: 'GET',
    });
  }

  /**
   * Gets summary data for chart from subestipulante proponent list.
   * Processes the list data to generate summary grouped by status.
   * @param identSubestipulante - Subestipulante identifier (required)
   * @param nmProponente - Proponent name (optional)
   * @param cdCpf - CPF code (optional)
   * @param cdCtrlCpf - CPF control digit (optional)
   * @param cdMatricula - Registration code (optional)
   * @returns AdhesionSummaryResponse with status counts and percentages
   */
  async getSummaryFromList(
    identSubestipulante: number,
    nmProponente?: string,
    cdCpf?: string,
    cdCtrlCpf?: string,
    cdMatricula?: number
  ): Promise<AdhesionSummaryResponse> {
    const data = await this.getSubestipulanteProponentList(
      identSubestipulante,
      nmProponente,
      cdCpf,
      cdCtrlCpf,
      cdMatricula,
      0,
      1000
    );

    return this.processSummaryFromList(data);
  }

  /**
   * Gets paginated list of PA proponent validation proposals.
   * Matches backend endpoint: /v1/bff/vida/proposta-adesao/obter-lista-validacao-pa-proponente
   * @param numeroApolice - Policy number (required)
   * @param dtInicio - Start date in yyyyMMdd format (required)
   * @param dtFim - End date in yyyyMMdd format (required)
   * @param identificadorSubestipulante - Subestipulante identifier (optional)
   * @param page - Page number (0-based, default: 0)
   * @param size - Page size (default: 6)
   */
  async getPAProponentValidationList(
    numeroApolice: string,
    dtInicio: string,
    dtFim: string,
    identificadorSubestipulante?: string,
    page?: number,
    size?: number
  ): Promise<ValidacaoPAProponenteListResponse> {
    let url = `${VIDA_ADHESION_PROPOSAL_ROUTES.GET_LIST_VALIDATION_PA_PROPOSITION}?numeroApolice=${encodeURIComponent(numeroApolice)}&dtInicio=${encodeURIComponent(dtInicio)}&dtFim=${encodeURIComponent(dtFim)}`;

    if (identificadorSubestipulante) {
      url += `&identificadorSubestipulante=${encodeURIComponent(identificadorSubestipulante)}`;
    }

    if (page !== undefined && page !== null) {
      url += `&page=${page}`;
    }

    if (size !== undefined && size !== null) {
      url += `&size=${size}`;
    }

    return this.fetch<ValidacaoPAProponenteListResponse>(url, {
      method: 'GET',
    });
  }

  /**
   * Gets PA opinion history for a proponent.
   * Matches backend endpoint: /v1/bff/vida/proposta-adesao/obter-historico-parecer-pa
   * @param identProponente - Proponent identifier (required)
   * @param numVersaoPa - PA version number (required)
   */
  async getPAOpinionHistory(
    identProponente: number,
    numVersaoPa: number
  ): Promise<HistoricoParecerPAResponse> {
    const url = `${VIDA_ADHESION_PROPOSAL_ROUTES.GET_HISTORY_PA_PROPOSAL}?identProponente=${encodeURIComponent(identProponente)}&numVersaoPa=${encodeURIComponent(numVersaoPa)}`;

    return this.fetch<HistoricoParecerPAResponse>(url, {
      method: 'GET',
    });
  }

  /**
   * Gets PA refusal reasons.
   * Matches backend endpoint: /v1/bff/vida/proposta-adesao/obter-motivo-recusa-pa
   * @param tipoRecusa - Refusal type (optional)
   * @param cdTipoRecusa - Refusal type code (optional)
   */
  async getPARefusalReasons(
    tipoRecusa?: string,
    cdTipoRecusa?: string
  ): Promise<MotivoRecusaPAResponse> {
    let url = VIDA_ADHESION_PROPOSAL_ROUTES.GET_REJECT_PA_PROPOSAL;

    const params: string[] = [];

    if (tipoRecusa) {
      params.push(`tipoRecusa=${encodeURIComponent(tipoRecusa)}`);
    }

    if (cdTipoRecusa) {
      params.push(`cdTipoRecusa=${encodeURIComponent(cdTipoRecusa)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.fetch<MotivoRecusaPAResponse>(url, {
      method: 'GET',
    });
  }
}
